import { useAuth0 } from '@auth0/auth0-react';
import { ArrowLeft, Bell, Building2, CircleDollarSign, ClipboardList, Layers3, Loader2, LogOut, Mail, Plus, RefreshCw, ScrollText, Send, ShieldCheck, Users, X, type LucideIcon } from 'lucide-react';
import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from './config/env';
import { useApiClient } from './lib/apiClient';
import { CLINIC_TYPE_OPTIONS, OrganizationDetailsPageV2, OrganizationsPageV2 } from './features/organizations/pages';
import { QuestionnairesPageV2, QuestionnaireTemplatesPageV2 } from './features/questionnaires/pages';
import { AuditLogPage } from './features/audit/page';
import { AuthLoginPage } from './auth/client';
import {
  createQuestionnaireTemplateDefinition,
  getQuestionnairePages,
  isQuestionVisible,
  isQuestionnairePageVisible,
  type QuestionnaireTemplateDefinition,
} from './features/questionnaires/shared';

type Session = {
  email: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string;
  accessLevel: 'SUPER_ADMIN';
};

type DashboardPayload = {
  metrics: {
    tenants: number;
    activeTenants: number;
    prospects: number;
    submittedQuestionnaires: number;
    mrr: number;
  };
  recentTenants: Array<{
    organizationId: string;
    displayName: string;
    slug: string;
    status: string;
    planCode: string;
    createdAt: string;
  }>;
  recentProspects: Array<{
    id: string;
    clinicName: string;
    status: string;
    contactName: string;
    contactEmail: string;
    updatedAt: string;
    questionnaireStatus: string | null;
  }>;
};

type TenantRow = {
  organizationId: string;
  displayName: string;
  legalName: string;
  slug: string;
  status: string;
  timezone: string;
  billingEmail: string | null;
  billingContactName: string | null;
  billingCountry?: string | null;
  planCode: string;
  billingInterval: string;
  trialEndsAt: string | null;
  createdAt: string;
  notes: string | null;
  organizationName: string;
  userCount: number;
  initialAdmin: { email: string; name: string; createdAt: string } | null;
  subscription: {
    id: string;
    status: string;
    currency: string;
    basePrice: number;
    overagePrice: number;
    balance: number;
    seatsIncluded: number;
    activeSeats: number;
    nextInvoiceDate: string | null;
  } | null;
};

type ProspectRow = {
  id: string;
  clinicName: string;
  contactFirstName: string;
  contactLastName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  preferredLanguage: 'en' | 'es' | 'it';
  status: string;
  source: string | null;
  specialty: string | null;
  clinicSize: number | null;
  country: string | null;
  timezone: string | null;
  notes: string | null;
  convertedOrganizationId: string | null;
  lastQuestionnaireSentAt: string | null;
  createdAt: string;
  updatedAt: string;
  latestQuestionnaire: {
    id: string;
    status: string;
    sentAt: string | null;
    openedAt: string | null;
    submittedAt: string | null;
    expiresAt: string | null;
    templateName?: string | null;
    templateId?: string | null;
    response?: {
      preferredSubdomain?: string;
      billingEmail?: string;
      clinicLegalName?: string;
      timezone?: string;
      country?: string;
      billingContactName?: string;
      notes?: string;
    };
  } | null;
  canConvert: boolean;
};

type BillingRow = {
  id: string;
  organizationId: string;
  organizationName: string;
  tenantSlug: string | null;
  status: string;
  planCode: string;
  billingInterval: string;
  currency: string;
  seatsIncluded: number;
  activeSeats: number;
  basePrice: number;
  overagePrice: number;
  balance: number;
  nextInvoiceDate: string | null;
  updatedAt: string;
  notes: string | null;
};

type QuestionnairePayload = {
  data: {
    questionnaireId: string;
    status: string;
    language?: 'en' | 'es' | 'it';
    templateId: string | null;
    templateName: string;
    templateDefinition: QuestionnaireTemplateDefinition;
    expiresAt: string | null;
    submittedAt: string | null;
    prospect: {
      clinicName: string;
      contactName: string;
      contactEmail: string;
    };
    response?: Record<string, any> | null;
  };
};

type QuestionnaireTemplateRow = {
  id: string;
  name: string;
  description: string | null;
  status: string;
  isDefault: boolean;
  definition: QuestionnaireTemplateDefinition;
  questionnaireCount: number;
  createdAt: string;
  updatedAt: string;
};

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: Bell },
  { to: '/audit', label: 'Audit Log', icon: ScrollText },
  {
    to: '/leads',
    label: 'Leads',
    icon: ClipboardList,
    children: [
      { to: '/questionnaires', label: 'Questionnaires', icon: Mail },
      { to: '/questionnaire-templates', label: 'Templates', icon: Layers3 },
    ],
  },
  { to: '/organizations', label: 'Organizations', icon: Building2 },
  { to: '/billing', label: 'Billing', icon: CircleDollarSign },
] as const;

const tenantStatusOptions = ['PROVISIONING', 'ACTIVE', 'PAST_DUE', 'SUSPENDED', 'CHURNED'];
const prospectStatusOptions = ['NEW', 'DEMO_SCHEDULED', 'QUESTIONNAIRE_SENT', 'QUESTIONNAIRE_SUBMITTED', 'QUALIFIED', 'NEGOTIATING', 'WON', 'LOST'];
const subscriptionStatusOptions = ['DRAFT', 'TRIALING', 'ACTIVE', 'PAST_DUE', 'SUSPENDED', 'CANCELLED'];

const inputClass = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-400';

function formatDate(value?: string | null) {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'n/a';
  return date.toLocaleDateString();
}

function formatCurrency(value: number, currency = 'USD') {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function pillClass(value: string) {
  const normalized = value.toUpperCase();
  if (['ACTIVE', 'WON', 'SUBMITTED', 'TRIALING'].includes(normalized)) return 'pill pill-success';
  if (['PAST_DUE', 'QUESTIONNAIRE_SENT', 'QUESTIONNAIRE_SUBMITTED', 'QUALIFIED'].includes(normalized)) return 'pill pill-warn';
  if (['SUSPENDED', 'LOST', 'CHURNED', 'CANCELLED', 'EXPIRED'].includes(normalized)) return 'pill pill-danger';
  return 'pill pill-info';
}

function App() {
  const location = useLocation();
  if (location.pathname === '/login') {
    return <AuthLoginPage variant="admin" />;
  }
  if (location.pathname.startsWith('/questionnaire/')) {
    return <PublicQuestionnairePage pathname={location.pathname} search={location.search} />;
  }
  return <ProtectedAdminPortal />;
}

function ProtectedAdminPortal() {
  const { isAuthenticated, isLoading, loginWithRedirect, logout, error } = useAuth0();
  const { fetchJson } = useApiClient();
  const [session, setSession] = useState<Session | null>(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname } }).catch(() => { });
    }
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      setSession(null);
      setLoadingSession(false);
      setSessionError(null);
      return;
    }

    let active = true;
    setLoadingSession(true);
    fetchJson<Session>('/platform-admin/session')
      .then((payload) => {
        if (!active) return;
        setSession(payload);
        setSessionError(null);
      })
      .catch((err) => {
        if (!active) return;
        setSession(null);
        setSessionError(String(err?.message || 'Unable to verify admin access.'));
      })
      .finally(() => {
        if (active) setLoadingSession(false);
      });

    return () => {
      active = false;
    };
  }, [fetchJson, isAuthenticated, isLoading]);

  if (isLoading || loadingSession) return <FullscreenState title="Loading admin portal..." />;
  if (error) return <FullscreenState title={`Auth error: ${error.message}`} danger />;
  if (!isAuthenticated) return <FullscreenState title="Redirecting to login..." />;
  if (!session) {
    return (
      <FullscreenState title="This account is not a super admin and cannot access the admin portal." danger>
        <button
          type="button"
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700"
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        >
          Sign out
        </button>
        {sessionError ? <div className="text-sm text-rose-600">{sessionError}</div> : null}
      </FullscreenState>
    );
  }

  return <AdminShell session={session} onLogout={() => logout({ logoutParams: { returnTo: window.location.origin } })} />;
}

function AdminShell({ session, onLogout }: { session: Session; onLogout: () => void }) {
  const location = useLocation();
  const isLeadGroupActive = location.pathname.startsWith('/leads')
    || location.pathname.startsWith('/questionnaires')
    || location.pathname.startsWith('/questionnaire-templates');

  return (
    <div className="min-h-screen bg-mist text-ink flex">
      <aside className="w-64 bg-white border-r border-slate-100 shadow-soft">
        <div className="px-4 py-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-bold text-lg">e</div>
            <div>
              <div className="text-2xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>
                elen1<span style={{ color: '#3b82f6' }}>.</span>
              </div>
              <p className="text-xs text-slate-500">Platform admin</p>
            </div>
          </div>
        </div>
        <nav className="p-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isGroup = 'children' in item;
            return (
              <div key={item.to} className="space-y-1">
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition ${isGroup && isLeadGroupActive ? 'bg-slate-900 text-white' : isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`
                  }
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </NavLink>
                {isGroup ? (
                  <div className="ml-4 space-y-1 border-l border-slate-100 pl-3">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      return (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          className={({ isActive }) =>
                            `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${isActive ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'
                            }`
                          }
                        >
                          <ChildIcon size={15} />
                          <span>{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>
      </aside>
      <div className="flex-1 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-100 px-6 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-slate-400">admin.elen1.com</p>
            <h1 className="text-lg font-semibold text-slate-900">Super Admin Console</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-right">
              <p className="text-sm font-semibold text-slate-900">{session.fullName}</p>
              <p className="text-xs text-slate-500">{session.email}</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-lime-700">{session.accessLevel}</p>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              onClick={onLogout}
            >
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        </header>
        <main className="p-6 lg:p-10 space-y-6">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/audit" element={<AuditLogPage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/leads/:leadId" element={<LeadDetailsPage />} />
            <Route path="/prospects" element={<Navigate to="/leads" replace />} />
            <Route path="/prospects/:leadId" element={<LeadAliasRedirect />} />
            <Route path="/organizations" element={<OrganizationsPageV2 />} />
            <Route path="/organizations/:organizationId" element={<OrganizationDetailsPageV2 />} />
            <Route path="/tenants" element={<TenantsPage />} />
            <Route path="/questionnaires" element={<QuestionnairesPageV2 />} />
            <Route path="/questionnaire-templates" element={<QuestionnaireTemplatesPageV2 />} />
            <Route path="/billing" element={<BillingPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function DashboardPage() {
  const { fetchJson } = useApiClient();
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchJson<DashboardPayload>('/platform-admin/dashboard')
      .then((payload) => {
        if (!active) return;
        setData(payload);
        setError(null);
      })
      .catch((err) => {
        if (!active) return;
        setError(String(err?.message || 'Unable to load dashboard.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fetchJson]);

  if (loading) return <SectionCard title="Dashboard"><LoadingLine /></SectionCard>;
  if (error || !data) return <SectionCard title="Dashboard"><ErrorText text={error || 'Unable to load dashboard.'} /></SectionCard>;

  const metrics = [
    { label: 'Organizations', value: data.metrics.tenants, icon: Building2 },
    { label: 'Active organizations', value: data.metrics.activeTenants, icon: Users },
    { label: 'Leads', value: data.metrics.prospects, icon: ClipboardList },
    { label: 'MRR', value: formatCurrency(data.metrics.mrr), icon: CircleDollarSign },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metrics.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className="card p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{item.label}</p>
                  <p className="mt-2 text-3xl font-semibold text-slate-900">{item.value}</p>
                </div>
                <div className="h-11 w-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
                  <Icon size={20} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <SectionCard title="Recent tenants">
          <div className="space-y-3">
            {data.recentTenants.map((item) => (
              <div key={item.organizationId} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-900">{item.displayName}</p>
                    <p className="text-sm text-slate-500">{item.slug}</p>
                  </div>
                  <span className={pillClass(item.status)}>{item.status}</span>
                </div>
                <p className="mt-3 text-sm text-slate-500">Plan {item.planCode} • Created {formatDate(item.createdAt)}</p>
              </div>
            ))}
          </div>
        </SectionCard>
        <SectionCard title="Recent leads">
          <div className="space-y-3">
            {data.recentProspects.map((item) => (
              <div key={item.id} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-900">{item.clinicName}</p>
                    <p className="text-sm text-slate-500">{item.contactName} • {item.contactEmail}</p>
                  </div>
                  <span className={pillClass(item.status)}>{item.status}</span>
                </div>
                <p className="mt-3 text-sm text-slate-500">
                  Questionnaire {item.questionnaireStatus || 'not sent'} • Updated {formatDate(item.updatedAt)}
                </p>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

type OrganizationRow = {
  id: string;
  name: string;
  timezone: string;
  createdAt: string;
  hasTenantAccount: boolean;
  tenantSlug: string | null;
  userCount: number;
  locationCount: number;
  patientCount: number;
};

type OrganizationUserRow = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roleName: string;
  active: boolean;
  createdAt: string;
};

function OrganizationsPage() {
  const { fetchJson } = useApiClient();
  const [rows, setRows] = useState<OrganizationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<OrganizationRow | null>(null);
  const [users, setUsers] = useState<OrganizationUserRow[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchJson<{ data: OrganizationRow[] }>('/platform-admin/organizations')
      .then((payload) => {
        setRows(payload.data);
        if (payload.data.length > 0) {
          setSelected(payload.data[0]);
        }
      })
      .finally(() => setLoading(false));
  }, [fetchJson]);

  useEffect(() => {
    if (!selected) {
      setUsers([]);
      return;
    }
    setLoadingUsers(true);
    fetchJson<{ data: OrganizationUserRow[] }>(`/platform-admin/organizations/${selected.id}/users`)
      .then((payload) => setUsers(payload.data))
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [selected, fetchJson]);

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <SectionCard title="Organizations directory">
          {loading ? <LoadingLine /> : (
            <div className="space-y-3">
              {rows.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => setSelected(item)}
                  className={`w-full text-left rounded-2xl border p-4 transition ${selected?.id === item.id ? 'border-slate-900 bg-slate-50' : 'border-slate-100 bg-white'}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{item.name}</p>
                      <p className="text-sm text-slate-500">ID: {item.id} • {item.timezone}</p>
                    </div>
                    <span className={pillClass(item.hasTenantAccount ? 'ACTIVE' : 'DRAFT')}>
                      {item.hasTenantAccount ? 'TENANT CONFIGURED' : 'RAW ORGANIZATION'}
                    </span>
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-4 text-sm text-slate-600">
                    <div>Slug: {item.tenantSlug || 'n/a'}</div>
                    <div>{item.userCount} users</div>
                    <div>{item.locationCount} locations • {item.patientCount} patients</div>
                    <div>Created {formatDate(item.createdAt)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title={`Users in ${selected?.name || 'Organization'}`}>
          {!selected ? (
            <div className="text-sm text-slate-500">Select an organization to view its users.</div>
          ) : loadingUsers ? (
            <LoadingLine />
          ) : users.length === 0 ? (
            <div className="text-sm text-slate-500">No users found in this organization.</div>
          ) : (
            <div className="space-y-3">
              {users.map((u) => (
                <div key={u.id} className="rounded-2xl border border-slate-100 bg-white p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{u.firstName} {u.lastName}</p>
                      <p className="text-sm text-slate-500">{u.email}</p>
                    </div>
                    <span className={pillClass(u.active ? 'ACTIVE' : 'SUSPENDED')}>
                      {u.active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </div>
                  <div className="mt-3 text-sm text-slate-600 flex gap-4">
                    <div>Role: <strong>{u.roleName}</strong></div>
                    <div>Joined {formatDate(u.createdAt)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function TenantsPage() {
  const { fetchJson, fetchApi } = useApiClient();
  const [rows, setRows] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [selected, setSelected] = useState<TenantRow | null>(null);
  const [form, setForm] = useState({
    displayName: '',
    slug: '',
    timezone: 'America/Toronto',
    billingEmail: '',
    planCode: 'enterprise',
    basePrice: '0',
    seatsIncluded: '10',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
  });

  const load = () => {
    setLoading(true);
    fetchJson<{ data: TenantRow[] }>('/platform-admin/tenants')
      .then((payload) => {
        setRows(payload.data);
        setSelected((current) => payload.data.find((item) => item.organizationId === current?.organizationId) || payload.data[0] || null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    const res = await fetchApi('/platform-admin/tenants', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        displayName: form.displayName,
        slug: form.slug || undefined,
        timezone: form.timezone,
        billingEmail: form.billingEmail || undefined,
        planCode: form.planCode,
        basePrice: Number(form.basePrice || 0),
        seatsIncluded: Number(form.seatsIncluded || 10),
        initialAdmin: {
          firstName: form.adminFirstName,
          lastName: form.adminLastName,
          email: form.adminEmail,
        },
      }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMessage(payload?.message || 'Unable to create tenant.');
      return;
    }
    setForm({
      displayName: '',
      slug: '',
      timezone: 'America/Toronto',
      billingEmail: '',
      planCode: 'enterprise',
      basePrice: '0',
      seatsIncluded: '10',
      adminFirstName: '',
      adminLastName: '',
      adminEmail: '',
    });
    setMessage(`Tenant created. Invite delivery mode: ${payload.inviteDeliveryMode || 'unknown'}.`);
    load();
  };

  const saveTenant = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    setMessage(null);
    const res = await fetchApi(`/platform-admin/tenants/${selected.organizationId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        displayName: selected.displayName,
        legalName: selected.legalName,
        slug: selected.slug,
        status: selected.status,
        timezone: selected.timezone,
        billingEmail: selected.billingEmail,
        billingContactName: selected.billingContactName,
        billingCountry: selected.billingCountry,
        billingInterval: selected.billingInterval,
        planCode: selected.planCode,
        trialEndsAt: selected.trialEndsAt || null,
        notes: selected.notes || null,
      }),
    });
    const payload = await res.json().catch(() => ({}));
    setMessage(res.ok ? 'Tenant updated.' : payload?.message || 'Unable to update tenant.');
    if (res.ok) load();
  };

  return (
    <div className="space-y-6">
      <SectionCard title="Create tenant">
        <form className="grid gap-4 lg:grid-cols-3" onSubmit={handleSubmit}>
          <Field label="Tenant name"><input className={inputClass} value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} required /></Field>
          <Field label="Slug"><input className={inputClass} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="demo-clinic" /></Field>
          <Field label="Timezone"><input className={inputClass} value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} required /></Field>
          <Field label="Billing email"><input className={inputClass} value={form.billingEmail} onChange={(e) => setForm({ ...form, billingEmail: e.target.value })} type="email" /></Field>
          <Field label="Plan"><input className={inputClass} value={form.planCode} onChange={(e) => setForm({ ...form, planCode: e.target.value })} /></Field>
          <Field label="Base price"><input className={inputClass} value={form.basePrice} onChange={(e) => setForm({ ...form, basePrice: e.target.value })} type="number" min="0" /></Field>
          <Field label="Seats included"><input className={inputClass} value={form.seatsIncluded} onChange={(e) => setForm({ ...form, seatsIncluded: e.target.value })} type="number" min="1" /></Field>
          <Field label="Admin first name"><input className={inputClass} value={form.adminFirstName} onChange={(e) => setForm({ ...form, adminFirstName: e.target.value })} required /></Field>
          <Field label="Admin last name"><input className={inputClass} value={form.adminLastName} onChange={(e) => setForm({ ...form, adminLastName: e.target.value })} required /></Field>
          <Field label="Admin email"><input className={inputClass} value={form.adminEmail} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })} type="email" required /></Field>
          <div className="lg:col-span-3 flex items-center justify-between gap-3">
            <div className="text-sm text-slate-500">The initial admin is provisioned in Cognito and receives the standard Elen1 invite.</div>
            <button className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white" type="submit">Create tenant</button>
          </div>
        </form>
        {message ? <div className="mt-4 text-sm text-slate-600">{message}</div> : null}
      </SectionCard>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <SectionCard title="Tenant directory">
          {loading ? <LoadingLine /> : (
            <div className="space-y-3">
              {rows.map((item) => (
                <button
                  key={item.organizationId}
                  type="button"
                  className={`w-full rounded-2xl border p-4 text-left transition ${selected?.organizationId === item.organizationId ? 'border-slate-900 bg-slate-50' : 'border-slate-100'}`}
                  onClick={() => setSelected(item)}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{item.displayName}</p>
                      <p className="text-sm text-slate-500">{item.slug} • {item.organizationName}</p>
                    </div>
                    <span className={pillClass(item.status)}>{item.status}</span>
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-4 text-sm text-slate-600">
                    <div>Plan {item.planCode}</div>
                    <div>{item.userCount} users</div>
                    <div>Billing {item.billingEmail || 'n/a'}</div>
                    <div>Created {formatDate(item.createdAt)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Edit tenant">
          {!selected ? (
            <div className="text-sm text-slate-500">Select a tenant to manage its account-level settings.</div>
          ) : (
            <form className="grid gap-4" onSubmit={saveTenant}>
              <Field label="Display name"><input className={inputClass} value={selected.displayName} onChange={(e) => setSelected({ ...selected, displayName: e.target.value })} /></Field>
              <Field label="Legal name"><input className={inputClass} value={selected.legalName} onChange={(e) => setSelected({ ...selected, legalName: e.target.value })} /></Field>
              <Field label="Slug"><input className={inputClass} value={selected.slug} onChange={(e) => setSelected({ ...selected, slug: e.target.value })} /></Field>
              <Field label="Status">
                <select className={inputClass} value={selected.status} onChange={(e) => setSelected({ ...selected, status: e.target.value })}>
                  {tenantStatusOptions.map((item) => <option key={item}>{item}</option>)}
                </select>
              </Field>
              <Field label="Timezone"><input className={inputClass} value={selected.timezone} onChange={(e) => setSelected({ ...selected, timezone: e.target.value })} /></Field>
              <Field label="Plan code"><input className={inputClass} value={selected.planCode} onChange={(e) => setSelected({ ...selected, planCode: e.target.value })} /></Field>
              <Field label="Billing interval">
                <select className={inputClass} value={selected.billingInterval} onChange={(e) => setSelected({ ...selected, billingInterval: e.target.value })}>
                  <option value="MONTHLY">MONTHLY</option>
                  <option value="YEARLY">YEARLY</option>
                </select>
              </Field>
              <Field label="Billing email"><input className={inputClass} value={selected.billingEmail || ''} onChange={(e) => setSelected({ ...selected, billingEmail: e.target.value || null })} /></Field>
              <Field label="Billing contact"><input className={inputClass} value={selected.billingContactName || ''} onChange={(e) => setSelected({ ...selected, billingContactName: e.target.value || null })} /></Field>
              <Field label="Billing country"><input className={inputClass} value={selected.billingCountry || ''} onChange={(e) => setSelected({ ...selected, billingCountry: e.target.value || null })} /></Field>
              <Field label="Trial ends at">
                <input className={inputClass} type="date" value={selected.trialEndsAt ? selected.trialEndsAt.slice(0, 10) : ''} onChange={(e) => setSelected({ ...selected, trialEndsAt: e.target.value ? new Date(e.target.value).toISOString() : null })} />
              </Field>
              <Field label="Notes"><textarea className={`${inputClass} min-h-[120px]`} value={selected.notes || ''} onChange={(e) => setSelected({ ...selected, notes: e.target.value || null })} /></Field>
              {selected.initialAdmin ? <div className="text-sm text-slate-500">Initial admin: {selected.initialAdmin.name} • {selected.initialAdmin.email}</div> : null}
              <button className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white" type="submit">Save tenant</button>
              {message ? <div className="text-sm text-slate-600">{message}</div> : null}
            </form>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

type LeadQuestionnaireSummary = {
  clinicLegalName?: string;
  preferredSubdomain?: string;
  timezone?: string;
  country?: string;
  billingContactName?: string;
  billingEmail?: string;
  clinicSize?: number;
  needsMigrationSupport?: boolean;
  requestedGoLiveDate?: string;
  notes?: string;
};

type LeadQuestionnaireRow = {
  id: string;
  prospectId: string;
  status: string;
  invitedEmail: string;
  sentAt: string | null;
  openedAt: string | null;
  submittedAt: string | null;
  expiresAt: string | null;
  templateName: string | null;
  templateId: string | null;
  templateVersion: number | null;
  createdAt: string;
  updatedAt: string;
  response: Record<string, any> | null;
  responseSummary: LeadQuestionnaireSummary | null;
};

type LeadDetailPayload = ProspectRow & {
  questionnaires: LeadQuestionnaireRow[];
};

type LeadCreateForm = {
  clinicName: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  preferredLanguage: 'en' | 'es' | 'it';
  specialty: string;
  source: string;
  clinicSize: string;
  country: string;
  timezone: string;
  notes: string;
};

function DialogShell({
  title,
  description,
  children,
  onClose,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-3xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            {description ? <p className="mt-1 text-sm text-slate-500">{description}</p> : null}
          </div>
          <button type="button" className="rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-600 hover:bg-slate-50" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="max-h-[80vh] overflow-y-auto px-6 py-5">
          {children}
        </div>
      </div>
    </div>
  );
}

function LeadAliasRedirect() {
  const { leadId } = useParams<{ leadId: string }>();
  return <Navigate to={leadId ? `/leads/${leadId}` : '/leads'} replace />;
}

function LeadsPage() {
  const { fetchJson, fetchApi } = useApiClient();
  const navigate = useNavigate();
  const [rows, setRows] = useState<ProspectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<LeadCreateForm>({
    clinicName: '',
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: '',
    preferredLanguage: 'en',
    specialty: '',
    source: 'demo',
    clinicSize: '',
    country: '',
    timezone: '',
    notes: '',
  });

  const load = () => {
    setLoading(true);
    fetchJson<{ data: ProspectRow[] }>('/platform-admin/prospects')
      .then((payload) => setRows(payload.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [fetchJson]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((item) => [
      item.clinicName,
      item.contactName,
      item.contactEmail,
      item.status,
      item.source,
      item.specialty,
      item.latestQuestionnaire?.status,
      item.latestQuestionnaire?.response?.preferredSubdomain,
    ].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [rows, search]);

  const createLead = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    const res = await fetchApi('/platform-admin/prospects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clinicName: createForm.clinicName,
        contactFirstName: createForm.contactFirstName,
        contactLastName: createForm.contactLastName,
        contactEmail: createForm.contactEmail,
        contactPhone: createForm.contactPhone || undefined,
        preferredLanguage: createForm.preferredLanguage,
        specialty: createForm.specialty || undefined,
        source: createForm.source || undefined,
        clinicSize: createForm.clinicSize ? Number(createForm.clinicSize) : undefined,
        country: createForm.country || undefined,
        timezone: createForm.timezone || undefined,
        notes: createForm.notes || undefined,
      }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMessage(payload?.message || 'Unable to create lead.');
      return;
    }
    setCreateOpen(false);
    setCreateForm({
      clinicName: '',
      contactFirstName: '',
      contactLastName: '',
      contactEmail: '',
      contactPhone: '',
      preferredLanguage: 'en',
      specialty: '',
      source: 'demo',
      clinicSize: '',
      country: '',
      timezone: '',
      notes: '',
    });
    load();
    navigate(`/leads/${payload?.data?.id}`);
  };

  return (
    <div className="space-y-6">
      <SectionCard title="Leads directory">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <input
              className={inputClass}
              placeholder="Search leads by clinic, contact, status, or questionnaire"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={load}>
            <RefreshCw size={15} />
            Refresh
          </button>
          <button type="button" className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm text-white" onClick={() => setCreateOpen(true)}>
            <Plus size={15} />
            New lead
          </button>
        </div>

        {loading ? (
          <LoadingLine />
        ) : filteredRows.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="No leads yet"
            text="Create the first lead to start tracking clinic onboarding and questionnaire activity."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-3 font-semibold">Lead</th>
                  <th className="px-3 py-3 font-semibold">Contact</th>
                  <th className="px-3 py-3 font-semibold">Status</th>
                  <th className="px-3 py-3 font-semibold">Questionnaire</th>
                  <th className="px-3 py-3 font-semibold">Updated</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((item) => (
                  <tr
                    key={item.id}
                    className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                    onClick={() => navigate(`/leads/${item.id}`)}
                  >
                    <td className="px-3 py-3">
                      <div className="font-semibold text-slate-900">{item.clinicName}</div>
                      <div className="text-xs text-slate-500">Source {item.source || 'n/a'} • Specialty {item.specialty || 'n/a'}</div>
                      <div className="text-xs text-slate-500">Language {item.preferredLanguage === 'es' ? 'Spanish' : item.preferredLanguage === 'it' ? 'Italian' : 'English'}</div>
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      <div>{item.contactName}</div>
                      <div className="text-xs text-slate-500">{item.contactEmail}</div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={pillClass(item.status)}>{item.status}</span>
                      {item.convertedOrganizationId ? <div className="mt-1 text-xs font-medium text-emerald-700">Converted</div> : null}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      <div>{item.latestQuestionnaire?.status || 'not sent'}</div>
                      <div className="text-xs text-slate-500">Sent {formatDate(item.lastQuestionnaireSentAt)}</div>
                    </td>
                    <td className="px-3 py-3 text-slate-700">{formatDate(item.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {createOpen ? (
        <DialogShell
          title="New lead"
          description="Capture a clinic prospect and start tracking their onboarding journey."
          onClose={() => setCreateOpen(false)}
        >
          <form className="grid gap-4 md:grid-cols-2" onSubmit={createLead}>
            <Field label="Clinic name"><input className={inputClass} value={createForm.clinicName} onChange={(e) => setCreateForm({ ...createForm, clinicName: e.target.value })} required /></Field>
            <Field label="Contact first name"><input className={inputClass} value={createForm.contactFirstName} onChange={(e) => setCreateForm({ ...createForm, contactFirstName: e.target.value })} required /></Field>
            <Field label="Contact last name"><input className={inputClass} value={createForm.contactLastName} onChange={(e) => setCreateForm({ ...createForm, contactLastName: e.target.value })} required /></Field>
            <Field label="Contact email"><input className={inputClass} type="email" value={createForm.contactEmail} onChange={(e) => setCreateForm({ ...createForm, contactEmail: e.target.value })} required /></Field>
            <Field label="Contact phone"><input className={inputClass} value={createForm.contactPhone} onChange={(e) => setCreateForm({ ...createForm, contactPhone: e.target.value })} /></Field>
            <Field label="Language">
              <select className={inputClass} value={createForm.preferredLanguage} onChange={(e) => setCreateForm({ ...createForm, preferredLanguage: e.target.value as LeadCreateForm['preferredLanguage'] })}>
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="it">Italian</option>
              </select>
            </Field>
            <Field label="Specialty"><input className={inputClass} value={createForm.specialty} onChange={(e) => setCreateForm({ ...createForm, specialty: e.target.value })} /></Field>
            <Field label="Source"><input className={inputClass} value={createForm.source} onChange={(e) => setCreateForm({ ...createForm, source: e.target.value })} /></Field>
            <Field label="Clinic size"><input className={inputClass} type="number" min="1" value={createForm.clinicSize} onChange={(e) => setCreateForm({ ...createForm, clinicSize: e.target.value })} /></Field>
            <Field label="Country"><input className={inputClass} value={createForm.country} onChange={(e) => setCreateForm({ ...createForm, country: e.target.value })} /></Field>
            <Field label="Timezone"><input className={inputClass} value={createForm.timezone} onChange={(e) => setCreateForm({ ...createForm, timezone: e.target.value })} /></Field>
            <div className="md:col-span-2">
              <Field label="Notes"><textarea className={`${inputClass} min-h-[120px]`} value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} /></Field>
            </div>
            <div className="md:col-span-2 flex justify-end gap-3">
              <button type="button" className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700" onClick={() => setCreateOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
                Create lead
              </button>
            </div>
          </form>
        </DialogShell>
      ) : null}

      {message ? <div className="text-sm text-slate-600">{message}</div> : null}
    </div>
  );
}

function LeadDetailsPage() {
  const { fetchJson, fetchApi } = useApiClient();
  const navigate = useNavigate();
  const { leadId } = useParams<{ leadId: string }>();
  const [lead, setLead] = useState<LeadDetailPayload | null>(null);
  const [templates, setTemplates] = useState<QuestionnaireTemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ invitedEmail: '', expiresInDays: '14', templateId: '' });
  const [convertConfig, setConvertConfig] = useState({ slug: '', clinicType: '', planCode: 'enterprise', billingInterval: 'MONTHLY', basePrice: '0', seatsIncluded: '10' });

  const defaultTemplateId = useMemo(() => {
    return templates.find((item) => item.isDefault && item.status === 'ACTIVE')
      || templates.find((item) => item.status === 'ACTIVE')
      || templates[0]
      || null;
  }, [templates]);

  const load = () => {
    if (!leadId) return;
    setLoading(true);
    Promise.all([
      fetchJson<{ data: LeadDetailPayload }>(`/platform-admin/prospects/${leadId}`),
      fetchJson<{ data: QuestionnaireTemplateRow[] }>('/platform-admin/questionnaire-templates'),
    ])
      .then(([leadPayload, templatesPayload]) => {
        setLead(leadPayload.data);
        setTemplates(templatesPayload.data);
        setSendForm({
          invitedEmail: leadPayload.data.contactEmail || '',
          expiresInDays: '14',
          templateId:
            leadPayload.data.latestQuestionnaire?.templateId
            || templatesPayload.data.find((item) => item.isDefault && item.status === 'ACTIVE')?.id
            || templatesPayload.data.find((item) => item.status === 'ACTIVE')?.id
            || templatesPayload.data[0]?.id
            || '',
        });
        setConvertConfig({
          slug: leadPayload.data.latestQuestionnaire?.response?.preferredSubdomain || '',
          clinicType: '',
          planCode: 'enterprise',
          billingInterval: 'MONTHLY',
          basePrice: '0',
          seatsIncluded: String(leadPayload.data.clinicSize || 10),
        });
      })
      .catch((err) => setMessage(String(err?.message || 'Unable to load lead.')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [fetchJson, leadId]);

  useEffect(() => {
    if (!lead) return;
    setSendForm({
      invitedEmail: lead.contactEmail || '',
      expiresInDays: '14',
      templateId: lead.latestQuestionnaire?.templateId || defaultTemplateId?.id || '',
    });
  }, [defaultTemplateId, lead?.id]);

  const saveLead = async (event: FormEvent) => {
    event.preventDefault();
    if (!lead) return;
    setMessage(null);
    const res = await fetchApi(`/platform-admin/prospects/${lead.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clinicName: lead.clinicName,
        contactFirstName: lead.contactFirstName,
        contactLastName: lead.contactLastName,
        contactEmail: lead.contactEmail,
        contactPhone: lead.contactPhone,
        preferredLanguage: lead.preferredLanguage,
        status: lead.status,
        source: lead.source,
        specialty: lead.specialty,
        clinicSize: lead.clinicSize,
        country: lead.country,
        timezone: lead.timezone,
        notes: lead.notes,
      }),
    });
    const payload = await res.json().catch(() => ({}));
    setMessage(res.ok ? 'Lead updated.' : payload?.message || 'Unable to update lead.');
    if (res.ok) load();
  };

  const sendQuestionnaire = async (event: FormEvent) => {
    event.preventDefault();
    if (!lead) return;
    setMessage(null);
    const res = await fetchApi(`/platform-admin/prospects/${lead.id}/questionnaires`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        invitedEmail: sendForm.invitedEmail || undefined,
        expiresInDays: Number(sendForm.expiresInDays || 14),
        templateId: sendForm.templateId || undefined,
      }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMessage(payload?.message || 'Unable to send questionnaire.');
      return;
    }
    setSendOpen(false);
    setMessage(`Questionnaire sent. Link: ${payload?.data?.formUrl}`);
    load();
  };

  const resendQuestionnaire = async (questionnaireId: string) => {
    setMessage(null);
    const res = await fetchApi(`/platform-admin/questionnaires/${questionnaireId}/resend`, {
      method: 'POST',
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMessage(payload?.message || 'Unable to resend questionnaire.');
      return;
    }
    if (payload?.data?.formUrl && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(payload.data.formUrl).catch(() => undefined);
    }
    setMessage(`Questionnaire resent. Link copied: ${payload?.data?.formUrl || 'generated'}`);
    load();
  };

  const deleteQuestionnaire = async (questionnaireId: string) => {
    setMessage(null);
    const res = await fetchApi(`/platform-admin/questionnaires/${questionnaireId}`, {
      method: 'DELETE',
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMessage(payload?.message || 'Unable to delete questionnaire.');
      return;
    }
    setMessage('Questionnaire deleted.');
    load();
  };

  const convert = async () => {
    if (!lead) return;
    setMessage(null);
    const res = await fetchApi(`/platform-admin/prospects/${lead.id}/convert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        slug: convertConfig.slug || undefined,
        clinicType: convertConfig.clinicType || undefined,
        planCode: convertConfig.planCode,
        billingInterval: convertConfig.billingInterval,
        basePrice: Number(convertConfig.basePrice || 0),
        seatsIncluded: Number(convertConfig.seatsIncluded || 10),
      }),
    });
    const payload = await res.json().catch(() => ({}));
    setMessage(res.ok ? `Lead converted. Tenant slug: ${payload?.slug}` : payload?.message || 'Unable to convert lead.');
    if (res.ok) load();
  };

  if (loading) {
    return <SectionCard title="Lead details"><LoadingLine /></SectionCard>;
  }

  if (!lead) {
    return <SectionCard title="Lead details"><ErrorText text={message || 'Unable to load lead.'} /></SectionCard>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm uppercase tracking-[0.25em] text-slate-400">Lead profile</p>
          <h2 className="text-3xl font-semibold text-slate-900">{lead.clinicName}</h2>
          <p className="mt-1 text-sm text-slate-500">{lead.contactName} • {lead.contactEmail}</p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => navigate('/leads')}>
            <ArrowLeft size={15} />
            Back to leads
          </button>
          <button type="button" className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm text-white" onClick={() => setSendOpen(true)}>
            <Send size={15} />
            Send questionnaire
          </button>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <SectionCard title="Lead details">
          <form className="grid gap-4 md:grid-cols-2" onSubmit={saveLead}>
            <Field label="Clinic name"><input className={inputClass} value={lead.clinicName} onChange={(e) => setLead({ ...lead, clinicName: e.target.value })} /></Field>
            <Field label="Status">
              <select className={inputClass} value={lead.status} onChange={(e) => setLead({ ...lead, status: e.target.value })}>
                {prospectStatusOptions.map((item) => <option key={item}>{item}</option>)}
              </select>
            </Field>
            <Field label="Contact first name"><input className={inputClass} value={lead.contactFirstName} onChange={(e) => setLead({ ...lead, contactFirstName: e.target.value, contactName: `${e.target.value} ${lead.contactLastName}`.trim() })} /></Field>
            <Field label="Contact last name"><input className={inputClass} value={lead.contactLastName} onChange={(e) => setLead({ ...lead, contactLastName: e.target.value, contactName: `${lead.contactFirstName} ${e.target.value}`.trim() })} /></Field>
            <Field label="Contact email"><input className={inputClass} value={lead.contactEmail} onChange={(e) => setLead({ ...lead, contactEmail: e.target.value })} /></Field>
            <Field label="Contact phone"><input className={inputClass} value={lead.contactPhone || ''} onChange={(e) => setLead({ ...lead, contactPhone: e.target.value || null })} /></Field>
            <Field label="Language">
              <select className={inputClass} value={lead.preferredLanguage} onChange={(e) => setLead({ ...lead, preferredLanguage: e.target.value as LeadCreateForm['preferredLanguage'] })}>
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="it">Italian</option>
              </select>
            </Field>
            <Field label="Source"><input className={inputClass} value={lead.source || ''} onChange={(e) => setLead({ ...lead, source: e.target.value || null })} /></Field>
            <Field label="Specialty"><input className={inputClass} value={lead.specialty || ''} onChange={(e) => setLead({ ...lead, specialty: e.target.value || null })} /></Field>
            <Field label="Clinic size"><input className={inputClass} type="number" min="1" value={lead.clinicSize || ''} onChange={(e) => setLead({ ...lead, clinicSize: e.target.value ? Number(e.target.value) : null })} /></Field>
            <Field label="Country"><input className={inputClass} value={lead.country || ''} onChange={(e) => setLead({ ...lead, country: e.target.value || null })} /></Field>
            <Field label="Timezone"><input className={inputClass} value={lead.timezone || ''} onChange={(e) => setLead({ ...lead, timezone: e.target.value || null })} /></Field>
            <div className="md:col-span-2">
              <Field label="Notes"><textarea className={`${inputClass} min-h-[120px]`} value={lead.notes || ''} onChange={(e) => setLead({ ...lead, notes: e.target.value || null })} /></Field>
            </div>
            {lead.convertedOrganizationId ? <div className="md:col-span-2 text-sm font-medium text-emerald-700">Converted to organization {lead.convertedOrganizationId}</div> : null}
            <div className="md:col-span-2 flex items-center justify-between gap-3">
              <div className="text-sm text-slate-500">Created {formatDate(lead.createdAt)} • Updated {formatDate(lead.updatedAt)}</div>
              <button type="submit" className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
                Save lead
              </button>
            </div>
          </form>
        </SectionCard>

        <div className="space-y-6">
          <SectionCard title="Questionnaires sent">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={load}>
                <RefreshCw size={15} />
                Refresh
              </button>
              <button type="button" className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm text-white" onClick={() => setSendOpen(true)}>
                <Plus size={15} />
                Send new questionnaire
              </button>
            </div>

            {lead.questionnaires.length === 0 ? (
              <EmptyState
                icon={Mail}
                title="No questionnaires yet"
                text="Send the first questionnaire to start collecting onboarding responses."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-3 font-semibold">Status</th>
                      <th className="px-3 py-3 font-semibold">Template</th>
                      <th className="px-3 py-3 font-semibold">Recipient</th>
                      <th className="px-3 py-3 font-semibold">Response</th>
                      <th className="px-3 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lead.questionnaires.map((item) => (
                      <tr key={item.id} className="border-b border-slate-100 align-top">
                        <td className="px-3 py-3">
                          <span className={pillClass(item.status)}>{item.status}</span>
                          <div className="mt-2 text-xs text-slate-500">
                            Sent {formatDate(item.sentAt)}<br />
                            Opened {formatDate(item.openedAt)}<br />
                            Submitted {formatDate(item.submittedAt)}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          <div className="font-medium text-slate-900">{item.templateName || 'Default template'}</div>
                          <div className="text-xs text-slate-500">Expires {formatDate(item.expiresAt)}</div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          <div>{item.invitedEmail}</div>
                          <div className="text-xs text-slate-500">Updated {formatDate(item.updatedAt)}</div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          <div className="font-medium text-slate-900">{item.responseSummary?.clinicLegalName || 'Awaiting response'}</div>
                          <div className="text-xs text-slate-500">Subdomain {item.responseSummary?.preferredSubdomain || 'n/a'}</div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                              onClick={() => resendQuestionnaire(item.id)}
                            >
                              Resend link
                            </button>
                            <button
                              type="button"
                              className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-50"
                              onClick={() => deleteQuestionnaire(item.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          {lead.canConvert ? (
            <SectionCard title="Convert to tenant">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Tenant slug"><input className={inputClass} value={convertConfig.slug} onChange={(e) => setConvertConfig({ ...convertConfig, slug: e.target.value })} /></Field>
                <Field label="Clinic type"><select className={inputClass} value={convertConfig.clinicType} onChange={(e) => setConvertConfig({ ...convertConfig, clinicType: e.target.value })}>{CLINIC_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></Field>
                <Field label="Plan code"><input className={inputClass} value={convertConfig.planCode} onChange={(e) => setConvertConfig({ ...convertConfig, planCode: e.target.value })} /></Field>
                <Field label="Billing interval">
                  <select className={inputClass} value={convertConfig.billingInterval} onChange={(e) => setConvertConfig({ ...convertConfig, billingInterval: e.target.value })}>
                    <option value="MONTHLY">MONTHLY</option>
                    <option value="YEARLY">YEARLY</option>
                  </select>
                </Field>
                <Field label="Base price"><input className={inputClass} type="number" min="0" value={convertConfig.basePrice} onChange={(e) => setConvertConfig({ ...convertConfig, basePrice: e.target.value })} /></Field>
                <Field label="Seats included"><input className={inputClass} type="number" min="1" value={convertConfig.seatsIncluded} onChange={(e) => setConvertConfig({ ...convertConfig, seatsIncluded: e.target.value })} /></Field>
                <div className="md:col-span-2 flex items-center justify-between gap-3 rounded-2xl bg-slate-50 p-4">
                  <div className="text-sm text-slate-600">
                    Conversion will use the latest submitted questionnaire as the default tenant data.
                  </div>
                  <button type="button" className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white" onClick={convert}>
                    Convert to tenant
                  </button>
                </div>
              </div>
            </SectionCard>
          ) : null}
        </div>
      </div>

      {sendOpen ? (
        <DialogShell
          title="Send questionnaire"
          description="Choose the recipient and template for the next onboarding questionnaire."
          onClose={() => setSendOpen(false)}
        >
          <form className="grid gap-4 md:grid-cols-2" onSubmit={sendQuestionnaire}>
            <Field label="Recipient email"><input className={inputClass} value={sendForm.invitedEmail} onChange={(e) => setSendForm({ ...sendForm, invitedEmail: e.target.value })} /></Field>
            <Field label="Expires in days"><input className={inputClass} type="number" min="1" max="60" value={sendForm.expiresInDays} onChange={(e) => setSendForm({ ...sendForm, expiresInDays: e.target.value })} /></Field>
            <div className="md:col-span-2">
              <Field label="Template">
                <select className={inputClass} value={sendForm.templateId} onChange={(e) => setSendForm({ ...sendForm, templateId: e.target.value })}>
                  <option value="">Use active default</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}{template.isDefault ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="md:col-span-2 flex justify-end gap-3">
              <button type="button" className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700" onClick={() => setSendOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
                Send questionnaire
              </button>
            </div>
          </form>
        </DialogShell>
      ) : null}

      {message ? <div className="text-sm text-slate-600">{message}</div> : null}
    </div>
  );
}

function BillingPage() {
  const { fetchJson, fetchApi } = useApiClient();
  const [rows, setRows] = useState<BillingRow[]>([]);
  const [selected, setSelected] = useState<BillingRow | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = () => {
    fetchJson<{ data: BillingRow[] }>('/platform-admin/billing').then((payload) => {
      setRows(payload.data);
      setSelected((current) => payload.data.find((item) => item.id === current?.id) || payload.data[0] || null);
    });
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    const res = await fetchApi(`/platform-admin/billing/${selected.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        status: selected.status,
        planCode: selected.planCode,
        billingInterval: selected.billingInterval,
        seatsIncluded: selected.seatsIncluded,
        activeSeats: selected.activeSeats,
        basePrice: selected.basePrice,
        overagePrice: selected.overagePrice,
        balance: selected.balance,
        nextInvoiceDate: selected.nextInvoiceDate || null,
        notes: selected.notes,
      }),
    });
    const payload = await res.json().catch(() => ({}));
    setMessage(res.ok ? 'Billing record updated.' : payload?.message || 'Unable to update billing.');
    if (res.ok) load();
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
      <SectionCard title="Subscriptions">
        <div className="space-y-3">
          {rows.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`w-full rounded-2xl border p-4 text-left transition ${selected?.id === item.id ? 'border-slate-900 bg-slate-50' : 'border-slate-100'}`}
              onClick={() => setSelected(item)}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{item.organizationName}</p>
                  <p className="text-sm text-slate-500">{item.tenantSlug || 'no-slug'} • {item.planCode}</p>
                </div>
                <span className={pillClass(item.status)}>{item.status}</span>
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-3 text-sm text-slate-600">
                <div>{formatCurrency(item.basePrice, item.currency)}</div>
                <div>{item.activeSeats}/{item.seatsIncluded} seats</div>
                <div>Balance {formatCurrency(item.balance, item.currency)}</div>
              </div>
            </button>
          ))}
        </div>
      </SectionCard>
      <SectionCard title="Edit subscription">
        {!selected ? <div className="text-sm text-slate-500">Select a subscription.</div> : (
          <form className="grid gap-4" onSubmit={save}>
            <Field label="Status">
              <select className={inputClass} value={selected.status} onChange={(e) => setSelected({ ...selected, status: e.target.value })}>
                {subscriptionStatusOptions.map((item) => <option key={item}>{item}</option>)}
              </select>
            </Field>
            <Field label="Plan code"><input className={inputClass} value={selected.planCode} onChange={(e) => setSelected({ ...selected, planCode: e.target.value })} /></Field>
            <Field label="Billing interval">
              <select className={inputClass} value={selected.billingInterval} onChange={(e) => setSelected({ ...selected, billingInterval: e.target.value })}>
                <option value="MONTHLY">MONTHLY</option>
                <option value="YEARLY">YEARLY</option>
              </select>
            </Field>
            <Field label="Base price"><input className={inputClass} type="number" value={selected.basePrice} onChange={(e) => setSelected({ ...selected, basePrice: Number(e.target.value) })} /></Field>
            <Field label="Overage price"><input className={inputClass} type="number" value={selected.overagePrice} onChange={(e) => setSelected({ ...selected, overagePrice: Number(e.target.value) })} /></Field>
            <Field label="Seats included"><input className={inputClass} type="number" value={selected.seatsIncluded} onChange={(e) => setSelected({ ...selected, seatsIncluded: Number(e.target.value) })} /></Field>
            <Field label="Active seats"><input className={inputClass} type="number" value={selected.activeSeats} onChange={(e) => setSelected({ ...selected, activeSeats: Number(e.target.value) })} /></Field>
            <Field label="Balance"><input className={inputClass} type="number" value={selected.balance} onChange={(e) => setSelected({ ...selected, balance: Number(e.target.value) })} /></Field>
            <Field label="Next invoice date">
              <input className={inputClass} type="date" value={selected.nextInvoiceDate ? selected.nextInvoiceDate.slice(0, 10) : ''} onChange={(e) => setSelected({ ...selected, nextInvoiceDate: e.target.value ? new Date(e.target.value).toISOString() : null })} />
            </Field>
            <Field label="Notes"><textarea className={`${inputClass} min-h-[112px]`} value={selected.notes || ''} onChange={(e) => setSelected({ ...selected, notes: e.target.value })} /></Field>
            <button className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white" type="submit">Save billing</button>
            {message ? <div className="text-sm text-slate-600">{message}</div> : null}
          </form>
        )}
      </SectionCard>
    </div>
  );
}

function PublicQuestionnairePage({ pathname, search }: { pathname: string; search: string }) {
  const token = pathname.startsWith('/questionnaire/')
    ? decodeURIComponent(pathname.slice('/questionnaire/'.length).split('/')[0] || '')
    : '';
  const initialLanguage = new URLSearchParams(search).get('language');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [template, setTemplate] = useState<QuestionnaireTemplateDefinition | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [language, setLanguage] = useState<'en' | 'es' | 'it'>(initialLanguage === 'es' || initialLanguage === 'it' ? initialLanguage : 'en');

  const copy = {
    en: {
      title: 'Elen1 onboarding',
      subtitle: 'Clinic setup questionnaire',
      intro: 'Share the details we need to provision your tenant and prepare go-live.',
      submitted: 'Questionnaire submitted. The Elen1 team can now review and convert your clinic into a tenant workspace.',
      loading: 'Loading questionnaire...',
      defaultSubmit: 'Submit questionnaire',
      languageLabel: 'Language',
      errorFallback: 'Unable to load questionnaire.',
    },
    es: {
      title: 'Incorporacion de Elen1',
      subtitle: 'Cuestionario de configuracion de la clinica',
      intro: 'Comparte los datos que necesitamos para aprovisionar tu tenant y preparar el lanzamiento.',
      submitted: 'Cuestionario enviado. El equipo de Elen1 ya puede revisarlo y convertir tu clinica en un tenant.',
      loading: 'Cargando cuestionario...',
      defaultSubmit: 'Enviar cuestionario',
      languageLabel: 'Idioma',
      errorFallback: 'No se pudo cargar el cuestionario.',
    },
    it: {
      title: 'Onboarding Elen1',
      subtitle: 'Questionario di configurazione della clinica',
      intro: 'Condividi i dettagli necessari per predisporre il tuo tenant e preparare il go-live.',
      submitted: 'Questionario inviato. Il team Elen1 puo ora rivederlo e convertire la tua clinica in un tenant.',
      loading: 'Caricamento questionario...',
      defaultSubmit: 'Invia questionario',
      languageLabel: 'Lingua',
      errorFallback: 'Impossibile caricare il questionario.',
    },
  }[language];

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE}/platform-admin/public/questionnaires/${token}`)
      .then(async (res) => {
        const payload = (await res.json().catch(() => ({}))) as QuestionnairePayload;
        if (!active) return;
        if (!res.ok) {
          setError((payload as any)?.message || copy.errorFallback);
          return;
        }
        const nextTemplate = payload.data.templateDefinition || createQuestionnaireTemplateDefinition();
        if (payload.data.language === 'es' || payload.data.language === 'it') {
          setLanguage(payload.data.language);
        }
        setTemplate(nextTemplate);
        const response = (payload.data.response || {}) as Record<string, any>;
        const initialAnswers: Record<string, any> = {};
        const pages = getQuestionnairePages(nextTemplate);
        for (const page of pages) {
          if (!isQuestionnairePageVisible(page, initialAnswers)) continue;
          for (const question of page.questions) {
            if (!isQuestionVisible(question, initialAnswers)) continue;
            const responseValue = response[question.key];
            if (responseValue !== undefined && responseValue !== null && responseValue !== '') {
              initialAnswers[question.key] = responseValue;
              continue;
            }
            if (question.defaultValue !== undefined) {
              initialAnswers[question.key] = question.defaultValue;
              continue;
            }
            if (question.type === 'checkbox') {
              initialAnswers[question.key] = false;
            } else if (question.key === 'billingContactName') {
              initialAnswers[question.key] = payload.data.prospect.contactName;
            } else if (question.key === 'billingEmail') {
              initialAnswers[question.key] = payload.data.prospect.contactEmail;
            } else if (question.key === 'timezone') {
              initialAnswers[question.key] = 'America/Toronto';
            } else {
              initialAnswers[question.key] = '';
            }
          }
        }
        setAnswers(initialAnswers);
        setDone(Boolean(payload.data.submittedAt));
      })
      .catch((err) => {
        if (!active) return;
        setError(String(err?.message || copy.errorFallback));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [copy.errorFallback, token]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const res = await fetch(`${API_BASE}/platform-admin/public/questionnaires/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError((payload as any)?.message || 'Unable to submit questionnaire.');
      return;
    }
    setDone(true);
  };

  return (
    <div className="min-h-screen bg-mist px-4 py-12">
      <div className="mx-auto max-w-3xl card p-8 space-y-6">
        <div>
          <p className="text-sm uppercase tracking-[0.25em] text-slate-400">{copy.title}</p>
          <h1 className="mt-2 text-3xl font-semibold text-slate-900">{template?.title || copy.subtitle}</h1>
          <p className="mt-2 text-slate-500">{template?.description || copy.intro}</p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
            <span>{copy.languageLabel}</span>
            <span className="font-semibold uppercase">{language}</span>
          </div>
        </div>
        {loading ? <LoadingLine /> : error ? <ErrorText text={error} /> : done ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-900">
            {copy.submitted}
          </div>
        ) : !template ? (
          <LoadingLine />
        ) : (
          <form className="grid gap-4 md:grid-cols-2" onSubmit={submit}>
            {getQuestionnairePages(template)
              .filter((page) => isQuestionnairePageVisible(page, answers))
              .map((page) => (
                <div key={page.key} className="md:col-span-2 rounded-2xl border border-slate-100 bg-slate-50/70 p-4 space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">{page.title}</h2>
                    {page.description ? <p className="mt-1 text-sm text-slate-500">{page.description}</p> : null}
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {page.questions.filter((question) => isQuestionVisible(question, answers)).map((question) => {
                      const spanClass = question.type === 'long_text' || question.type === 'select' ? 'md:col-span-2' : '';
                      const validation = question.validation || {};
                      return (
                        <div key={question.key} className={spanClass}>
                          <Field label={question.label}>
                            {question.type === 'long_text' ? (
                              <textarea
                                className={`${inputClass} min-h-[140px]`}
                                value={answers[question.key] ?? ''}
                                onChange={(e) => setAnswers({ ...answers, [question.key]: e.target.value })}
                                placeholder={question.placeholder || undefined}
                                required={question.required}
                                minLength={validation.minLength}
                                maxLength={validation.maxLength}
                              />
                            ) : question.type === 'checkbox' ? (
                              <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                                <input
                                  type="checkbox"
                                  checked={Boolean(answers[question.key])}
                                  onChange={(e) => setAnswers({ ...answers, [question.key]: e.target.checked })}
                                />
                                {question.helpText || 'Check to confirm'}
                              </label>
                            ) : question.type === 'select' ? (
                              <select
                                className={inputClass}
                                value={answers[question.key] ?? ''}
                                onChange={(e) => setAnswers({ ...answers, [question.key]: e.target.value })}
                                required={question.required}
                              >
                                <option value="">{question.placeholder || 'Select an option'}</option>
                                {(question.options || []).map((option) => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                className={inputClass}
                                value={answers[question.key] ?? ''}
                                onChange={(e) => setAnswers({ ...answers, [question.key]: e.target.value })}
                                placeholder={question.placeholder || undefined}
                                type={question.type === 'number' ? 'number' : question.type === 'date' ? 'date' : question.type === 'email' ? 'email' : 'text'}
                                required={question.required}
                                min={question.type === 'number' ? validation.minValue : undefined}
                                max={question.type === 'number' ? validation.maxValue : undefined}
                                minLength={question.type !== 'number' ? validation.minLength : undefined}
                                maxLength={question.type !== 'number' ? validation.maxLength : undefined}
                                pattern={question.type !== 'number' ? validation.pattern : undefined}
                              />
                            )}
                          </Field>
                          {question.helpText && question.type !== 'checkbox' ? <p className="mt-2 text-xs text-slate-500">{question.helpText}</p> : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            <div className="md:col-span-2 flex justify-end">
              <button className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white" type="submit">
                {template.submitLabel || copy.defaultSubmit}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="card p-5 lg:p-6">
      <div className="mb-5 flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-2 block font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}

function FullscreenState({ title, children, danger }: { title: string; children?: ReactNode; danger?: boolean }) {
  return (
    <div className="min-h-screen bg-mist flex items-center justify-center px-4">
      <div className="card w-full max-w-lg p-8 text-center space-y-4">
        <p className={`text-lg font-semibold ${danger ? 'text-rose-600' : 'text-slate-900'}`}>{title}</p>
        {children}
      </div>
    </div>
  );
}

function LoadingLine() {
  return (
    <div className="inline-flex items-center gap-2 text-sm text-slate-500">
      <Loader2 size={16} className="animate-spin" />
      Loading...
    </div>
  );
}

function ErrorText({ text }: { text: string }) {
  return <div className="text-sm text-rose-600">{text}</div>;
}

function EmptyState({ icon: Icon, title, text }: { icon: LucideIcon; title: string; text: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-6 text-center">
      <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white">
        <Icon size={18} />
      </div>
      <div className="text-base font-semibold text-slate-900">{title}</div>
      <div className="mt-1 text-sm text-slate-500">{text}</div>
    </div>
  );
}

export default App;
